import { type FilterManagerFilter } from '@webapp/components/FilterManager/types'
import { type IBudgetCustomFieldConditionValue } from '@webapp/platform/budgets'

export type BudgetCategoryListCustomFieldFilterKey = `custom_field_${number}`

export const CUSTOM_FIELD_FILTER_ROW_COUNT = 3

const CUSTOM_FIELD_FILTER_KEY_PATTERN = /^custom_field_(\d+)$/

export const getCustomFieldFilterKey = (
  customFieldId: number
): BudgetCategoryListCustomFieldFilterKey => `custom_field_${customFieldId}`

export const isCustomFieldFilterKey = (
  key: string
): key is BudgetCategoryListCustomFieldFilterKey =>
  CUSTOM_FIELD_FILTER_KEY_PATTERN.test(key)

export const isCustomFieldFilter = (
  filter: FilterManagerFilter
): filter is FilterManagerFilter<IBudgetCustomFieldConditionValue> & {
  key: BudgetCategoryListCustomFieldFilterKey
} => isCustomFieldFilterKey(filter.key)

export const getCustomFieldIdFromFilterKey = (
  key: BudgetCategoryListCustomFieldFilterKey
) => Number(key.match(CUSTOM_FIELD_FILTER_KEY_PATTERN)[1])

const CUSTOM_FIELD_VALUE_SEPARATOR = ','
const CUSTOM_FIELD_VALUE_ESCAPE = '\\'

const escapeCustomFieldValue = (value: string) =>
  value.replace(
    /[\\,]/g,
    (character) => `${CUSTOM_FIELD_VALUE_ESCAPE}${character}`
  )

/**
 * The backend reads one comma-separated param for each field. It does not
 * read the `key[]=` form that the list request sends for an array. A comma
 * or backslash inside a value is escaped with a backslash.
 */
export const toCustomFieldFilter = (
  customFieldCondition: IBudgetCustomFieldConditionValue
): FilterManagerFilter<IBudgetCustomFieldConditionValue> => ({
  key: getCustomFieldFilterKey(customFieldCondition.field.id),
  paramValue: customFieldCondition.values
    .map(escapeCustomFieldValue)
    .join(CUSTOM_FIELD_VALUE_SEPARATOR),
  value: customFieldCondition,
})

const splitEscapedCustomFieldParamValue = (paramValue: string) => {
  const values: string[] = []
  let currentValue = ''
  let isEscaped = false

  for (const character of paramValue) {
    if (isEscaped) {
      currentValue += character
      isEscaped = false
    } else if (character === CUSTOM_FIELD_VALUE_ESCAPE) {
      isEscaped = true
    } else if (character === CUSTOM_FIELD_VALUE_SEPARATOR) {
      values.push(currentValue)
      currentValue = ''
    } else {
      currentValue += character
    }
  }

  // A trailing backslash escapes nothing, so keep it as text.
  values.push(
    isEscaped ? `${currentValue}${CUSTOM_FIELD_VALUE_ESCAPE}` : currentValue
  )

  return values
}

export const parseCustomFieldParamValue = (paramValue: string | string[]) =>
  (Array.isArray(paramValue) ? paramValue : [paramValue])
    .reduce<
      string[]
    >((values, value) => [...values, ...splitEscapedCustomFieldParamValue(value)], [])
    .filter(Boolean)
